/* eslint-disable no-unused-vars */
import React, {
    useState, useContext, useCallback, useMemo,
    useEffect
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import { PaymentComponent } from 'tdic-components-platform-react';
import LocalDateUtil from 'gw-portals-util-js/LocalDateUtil';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Tooltip,
    IconButton,
    Loader
} from '@jutro/components';
import metadata from './AutopayComponent.metadata.json5';
import messages from './AutopayComponent.messages';
import styles from './AutopayComponent.module.scss';

const accountNumberRegix = new RegExp('^[0-9]*$');

function AutopayComponent({
    // eslint-disable-next-line no-unused-vars
    authHeader, policyNumber, offeringName, user_name: userName, email,
    tncTransactionTypesSubTypes, terms, isOpen, onResolve
}) {
    const translator = useContext(TranslatorContext);
    const [statusMessage, setStatusMessage] = useState();
    const [isLoading, setLoadingState] = useState(false);
    const [emailID, setEmailID] = useState('');
    const [hasExistingEmail, setHasExistingEmail] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    const [emailValidationError, setEmailValidationError] = useState('');
    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );
    const viewModelService = useContext(ViewModelServiceContext);
    const AUTO_PAY_PLAN_CODE = 'Monthly APW (12 Pay)';
    const PAYMENNT_METHOD = 'ach';

    const accountBankDetailVM = viewModelService.create(
        { bankAccountType: 'checking' },
        'bc',
        'edge.capabilities.billing.dto.AccountBankDetailsDTO'
    );

    const [formData, updateFormData] = useState({
        tncAccepted: false,
        bankAccountData: accountBankDetailVM,
    });

    const onCancelClick = useCallback(() => {
        return onResolve();
    }, [onResolve]);

    const invokeTnCCall = useCallback(() => {
        TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''),
            tncTransactionTypesSubTypes, 'ChangePaymentPlan', 'ChangePaymentPlan',
            authHeader, policyNumber, userName, email);
    }, [terms, tncTransactionTypesSubTypes, authHeader, policyNumber, userName, email]);

    const onActionClick = useCallback(() => {
        if (!statusMessage) {
            setLoadingState(true);
            const PaymentPlan = {
                policyNumber: policyNumber,
                PaymentPlan: AUTO_PAY_PLAN_CODE
            };
            const PaymentInstrument = {
                PaymentMethod: PAYMENNT_METHOD,
                BankAccountData: _.get(formData, 'bankAccountData.value'),
            };
            const requestPayload = { PaymentPlan, PaymentInstrument };
            BillingService.changePaymentPlanAndCreateInstrument(requestPayload,
                authHeader)
                .then((reponse) => {
                    if (reponse === true) {
                        invokeTnCCall();
                        setStatusMessage('successmessage');
                        setLoadingState(false);
                    }
                })
                .catch(() => {
                    setStatusMessage('failureMessage');
                    setLoadingState(false);
                });
        } else {
            onResolve(true);
        }
    }, [statusMessage, policyNumber, formData, BillingService, authHeader,
        invokeTnCCall, onResolve]);

    const handleChange = useCallback((value, changedPath) => {
        const data = { ...formData };
        _.set(data, changedPath, value);
        updateFormData(data);
    }, [formData]);

    const handleChangeTnc = useCallback((value) => {
        const data = { ...formData };
        _.set(data, 'tncAccepted', value);
        updateFormData(data);
    }, [formData]);

    const disableActionButton = useCallback(() => {
        const bankDetails = _.get(formData, 'bankAccountData');
        if (!_.get(formData, 'tncAccepted') || !bankDetails || !bankDetails.aspects.subtreeValid || isLoading) {
            return true;
        }
        if (!emailSubmitted) {
            return true;
        }
        return false;
    }, [formData, isLoading, emailSubmitted]);

    const getMaskedAccountNumber = useCallback((accountNumber) => {
        if (accountNumber !== undefined && accountNumber.length >= 4) {
            return `*${accountNumber.substring(accountNumber.length - 4)}`;
        }
        return accountNumber;
    }, []);

    const maskAccountNumber = useCallback((evnt, acctNumber) => {
        const accNum = acctNumber.value;
        const maskedAccountNumber = getMaskedAccountNumber(accNum);
        const bankaccountDetail = _.get(formData, 'bankAccountData');
        _.set(bankaccountDetail, 'value.maskedBankAccountNumber_TDIC', maskedAccountNumber);
        _.set(bankaccountDetail, 'value.bankAccountNumber', accNum);
        if (accNum === undefined || !accNum.startsWith('*')) {
            _.set(bankaccountDetail, 'value.bankAccountNumber', accNum);
        }
        updateFormData({ bankAccountData: bankaccountDetail });
    }, [formData, getMaskedAccountNumber]);

    const updateMaskedAccountNumber = useCallback((value) => {
        const bankaccountDetail = _.get(formData, 'bankAccountData');
        const acountNumb = _.get(bankaccountDetail, 'value.maskedBankAccountNumber_TDIC');
        if (accountNumberRegix.test(value) || acountNumb.length - 1 === value.length) {
            _.set(bankaccountDetail, 'value.maskedBankAccountNumber_TDIC', value);
            updateFormData({ bankAccountData: bankaccountDetail });
        }
    }, [formData]);

    const fetchEmailAddress = useCallback(async () => {
        if (!policyNumber || !BillingService) return;
        try {
            setIsEmailLoading(true);
            const emailResponse = await BillingService.getEmailAddress(policyNumber, authHeader);
            const emaill = _.get(emailResponse, 'result', null) || emailResponse || '';
            if (emaill && emaill.trim()) {
                setEmailID(emaill);
                setHasExistingEmail(true);
                setEmailSubmitted(true);
            } else {
                setHasExistingEmail(false);
                setEmailSubmitted(false);
            }
        } catch (error) {
            setHasExistingEmail(false);
            setEmailSubmitted(false);
        } finally {
            setIsEmailLoading(false);
        }
    }, [BillingService, authHeader, policyNumber]);

    useEffect(() => {
        if (policyNumber) {
            fetchEmailAddress();
        }
    }, [fetchEmailAddress, policyNumber]);

    const validateEmail = useCallback((emailId) => { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailId); }, []);

    const handleChangeEmailSetup = useCallback((value) => {
        setEmailID(value);
        setEmailValidationError('');
        setStatusMessage('');
    }, []);

    const updateSubmitValidEmailSetup = useCallback(async () => {
        const currentEmail = emailID;
        setEmailValidationError('');
        setStatusMessage('');
        if (!currentEmail || !currentEmail.trim()) {
            setEmailValidationError('invalidEmailFormat');
            return false;
        }
        if (!validateEmail(currentEmail)) {
            setEmailValidationError('invalidEmailFormat');
            return false;
        }
        setIsEmailLoading(true);
        try {
            const result = await BillingService.setEmailAddress(policyNumber,
                currentEmail, authHeader);
            if (result) {
                setHasExistingEmail(true);
                setEmailSubmitted(true);
                console.log('Email successfully submitted');
                return true;
            }
            setStatusMessage('failureMessage');
            setEmailSubmitted(false);
            return false;
        } catch (error) {
            console.error('Error in updateSubmitValidEmailSetup:', error);
            setStatusMessage('failureMessage');
            setEmailSubmitted(false);
            return false;
        } finally {
            setIsEmailLoading(false);
        }
    }, [emailID, validateEmail, BillingService, policyNumber, authHeader]);

    const history = useHistory();
    const handlebackToPreviousBillingState = useCallback(() => {
        onResolve();
    }, [history]);

    const handlePrint = () => {
        window.print();
    };

    const overrideProps = {
        selectPaymentPlan: {
            value: AUTO_PAY_PLAN_CODE
        },
        setupAutomaticPaymentPaymentScheduleId: {
            value: 'Monthly'
        },
        setupAutomaticPaymentStartingDateId: {
            value: LocalDateUtil.firstDayOfMonth()
        },
        termsOfUseCheckbox: {
            label: messages.tncLabel,
            onValueChange: handleChangeTnc,
            value: _.get(formData, 'tncAccepted')
        },
        setUpAutomaticPaymentContainerId: {
            visible: !statusMessage
        },
        autoPayConfirmationDiv: {
            visible: statusMessage
        },
        informationMessage: {
            content: statusMessage ? translator(messages[statusMessage], { policyNumber }) : ''
        },
        enterValidEmailAddressSetup: {
            value: emailID,
            onValueChange: handleChangeEmailSetup,
            placeholder: hasExistingEmail ? '' : 'Enter a valid email address',
            className: hasExistingEmail
                ? `${styles.enterValidEmailAddressAPW} ${styles.emailReadOnly}`
                : styles.enterValidEmailAddressAPW,
            readOnly: hasExistingEmail
        },
        emailValidationErrorMessageAPW: {
            visible: emailValidationError === 'invalidEmailFormat',
            content: 'Please enter a valid email address',
            className: styles.emailValidationErrorMessageAPW
        },
        submitValidEmailSetup: {
            disabled: isEmailLoading || !emailID.trim() || hasExistingEmail,
            onClick: updateSubmitValidEmailSetup,
        },
        backEmailButtonAPW: {
            disabled: isEmailLoading,
            onClick: onCancelClick,
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
        },
        resolveCallbackMap: {
            maskAccountNumber: maskAccountNumber,
            updateMaskedAccountNumber: updateMaskedAccountNumber,
            handleChangeEmailSetup: handleChangeEmailSetup,
            updateSubmitValidEmailSetup: updateSubmitValidEmailSetup,
            handlebackToPreviousBillingState: handlebackToPreviousBillingState
        }
    };
    return (
        <ModalNext isOpen={isOpen}>
            <ModalHeader
                title={`${offeringName} (${policyNumber})`}
            />
            <ModalBody>
                {isLoading ? (
                    <Loader loaded={!isLoading} />
                ) : (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={formData}
                        onValueChange={handleChange}
                        overrideProps={overrideProps}
                        classNameMap={resolvers.resolveClassNameMap}
                        componentMap={resolvers.resolveComponentMap}
                        callbackMap={resolvers.resolveCallbackMap}
                    />
                )}
            </ModalBody>
            <ModalFooter>
                { !statusMessage ? (
                    <div>
                        <Button onClick={handlePrint} type="tertiary" disabled={isLoading}>
                            {messages.printAuthorization}
                        </Button>
                        <Button onClick={onCancelClick} type="secondary" disabled={isLoading}>
                            {messages.cancel}
                        </Button>
                    </div>
                ) : null }
                <Button
                    onClick={onActionClick}
                    disabled={disableActionButton()}
                >
                    {statusMessage ? messages.ok : messages.submit}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

AutopayComponent.propTypes = {
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    }).isRequired,
    policyNumber: PropTypes.string.isRequired,
    offeringName: PropTypes.string.isRequired,
    user_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    tncTransactionTypesSubTypes: PropTypes.shape({}).isRequired,
    terms: PropTypes.shape({}).isRequired,
    onResolve: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired
};

export default withAuthenticationContext(AutopayComponent);
